import { supabase, auth } from './supabase';
import type { TranscriptionHistoryItem, TranscriptVersion } from '../types';

// ── Exported types ────────────────────────────────────────────────────────────

export type { TranscriptVersion };

export interface TranscriptComment {
  id?: string;
  transcriptId: string;
  selectedText: string;
  startOffset: number;
  endOffset: number;
  comment: string;
  createdAt: number;
}

export const addToHistory = async (
  fileNameOrItem: string | TranscriptionHistoryItem,
  transcript?: string,
  duration?: number,
  id?: string,
  file?: File,
): Promise<void> => {
  const item: TranscriptionHistoryItem = typeof fileNameOrItem === 'string'
    ? {
        id: id || crypto.randomUUID(),
        fileName: fileNameOrItem,
        transcript: transcript || '',
        timestamp: Date.now(),
        duration: duration || 0,
        mediaType: file?.type,
      }
    : fileNameOrItem;
  await saveTranscriptToDb(item).catch(console.error);
};

export const updateHistoryItem = async (
  id: string,
  updates: Partial<TranscriptionHistoryItem> | string,
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) return;
  const parsedUpdates: Partial<TranscriptionHistoryItem> =
    typeof updates === 'string' ? { transcript: updates } : updates;
  const row: Record<string, unknown> = {};
  if (parsedUpdates.transcript !== undefined) row.transcript = parsedUpdates.transcript;
  if (parsedUpdates.fileName !== undefined) row.file_name = parsedUpdates.fileName;
  if (parsedUpdates.folderId !== undefined) row.folder_id = parsedUpdates.folderId;
  if (parsedUpdates.shareId !== undefined) row.share_id = parsedUpdates.shareId;
  if (Object.keys(row).length === 0) return;
  const { error } = await supabase
    .from('transcripts')
    .update(row)
    .eq('id', id)
    .eq('user_id', user.id);
  if (error) console.error(error);
};

export const deleteFromHistory = async (id: string): Promise<void> => {
  await deleteTranscriptFromDb(id).catch(console.error);
};

// ── Row ↔ TranscriptionHistoryItem mapping ────────────────────────────────────

function rowToItem(row: Record<string, unknown>): TranscriptionHistoryItem {
  return {
    id: row.id as string,
    fileName: row.file_name as string,
    transcript: row.transcript as string,
    timestamp: row.timestamp as number,
    duration: row.duration as number,
    mediaData: row.media_data as string | undefined,
    mediaType: row.media_type as string | undefined,
    storageUrl: row.storage_url as string | undefined,
    shareId: row.share_id as string | undefined,
    folderId: row.folder_id as string | null | undefined,
  };
}

function itemToRow(item: TranscriptionHistoryItem, userId: string): Record<string, unknown> {
  return {
    id: item.id,
    user_id: userId,
    file_name: item.fileName,
    transcript: item.transcript,
    timestamp: item.timestamp,
    duration: item.duration,
    media_data: item.mediaData ?? null,
    media_type: item.mediaType ?? null,
    storage_url: item.storageUrl ?? null,
    share_id: item.shareId ?? null,
    folder_id: item.folderId ?? null,
  };
}

export const fetchUserTranscripts = async (uid: string): Promise<TranscriptionHistoryItem[]> => {
  const { data, error } = await supabase
    .from('transcripts')
    .select('id, file_name, transcript, timestamp, duration, media_data, media_type, storage_url, share_id, folder_id')
    .eq('user_id', uid)
    .is('deleted_at', null)
    .order('timestamp', { ascending: false });
  if (error) throw error;
  return (data ?? []).map(rowToItem);
};


export const saveTranscriptToDb = async (item: TranscriptionHistoryItem): Promise<void> => {
  const user = auth.currentUser;
  if (!user) return;
  const { error } = await supabase
    .from('transcripts')
    .upsert(itemToRow(item, user.id));
  if (error) throw error;
};

export const deleteTranscriptFromDb = async (transcriptId: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user) return;
  const { error } = await supabase
    .from('transcripts')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', transcriptId)
    .eq('user_id', user.id);
  if (error) throw error;
};

// ── Share links ───────────────────────────────────────────────────────────────

export const fetchTranscriptByShareId = async (
  shareId: string,
): Promise<TranscriptionHistoryItem | null> => {
  const { data, error } = await supabase
    .from('transcripts')
    .select('*')
    .eq('share_id', shareId)
    .is('deleted_at', null)
    .single();
  if (error || !data) return null;
  return rowToItem(data as Record<string, unknown>);
};

// ── Media storage ─────────────────────────────────────────────────────────────

export const uploadTranscriptMedia = async (
  transcriptId: string | undefined,
  file: File,
): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user || !transcriptId) return null;

  const ext = file.name.split('.').pop() || 'bin';
  const path = `${user.id}/${transcriptId}.${ext}`;

  const { error } = await supabase.storage
    .from('transcripts')
    .upload(path, file, { upsert: true });
  if (error) throw error;

  const { data: urlData } = supabase.storage.from('transcripts').getPublicUrl(path);
  const publicUrl = urlData.publicUrl;

  // Update the transcript row with the storage URL
  await supabase
    .from('transcripts')
    .update({ storage_url: publicUrl, media_data: null })
    .eq('id', transcriptId)
    .eq('user_id', user.id);

  return publicUrl;
};

// Keep old name for any remaining imports
export const uploadMediaToStorage = uploadTranscriptMedia;

// ── Word timestamps ───────────────────────────────────────────────────────────

export const getWordTimestamps = async (
  transcriptId: string,
): Promise<Record<string, unknown> | null> => {
  const { data, error } = await supabase
    .from('transcripts')
    .select('word_timestamps')
    .eq('id', transcriptId)
    .single();
  if (error || !data) return null;
  return data.word_timestamps as Record<string, unknown> | null;
};

export const saveWordTimestamps = async (
  transcriptId: string,
  timestamps: Record<string, unknown>,
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) return;
  const { error } = await supabase
    .from('transcripts')
    .update({ word_timestamps: timestamps })
    .eq('id', transcriptId)
    .eq('user_id', user.id);
  if (error) throw error;
};

// ── Speaker labels ────────────────────────────────────────────────────────────

export const getSpeakerLabels = async (
  transcriptId: string,
): Promise<Record<number, string> | null> => {
  const { data, error } = await supabase
    .from('transcripts')
    .select('speaker_labels')
    .eq('id', transcriptId)
    .single();
  if (error || !data) return null;
  return data.speaker_labels as Record<number, string> | null;
};

export const saveSpeakerLabels = async (
  transcriptId: string,
  labels: Record<number, string>,
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) return;
  const { error } = await supabase
    .from('transcripts')
    .update({ speaker_labels: labels })
    .eq('id', transcriptId)
    .eq('user_id', user.id);
  if (error) throw error;
};

// ── Version history ───────────────────────────────────────────────────────────

export const fetchVersions = async (transcriptId: string): Promise<TranscriptVersion[]> => {
  const user = auth.currentUser;
  if (!user) return [];
  const { data, error } = await supabase
    .from('versions')
    .select('*')
    .eq('transcript_id', transcriptId)
    .eq('user_id', user.id)
    .order('saved_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map(row => ({
    id: row.id as string,
    transcript: row.transcript as string,
    savedAt: row.saved_at as number,
    label: row.label as string | undefined,
  }));
};

export const saveVersion = async (
  transcriptId: string,
  transcript: string,
  label?: string,
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) return;
  const { error } = await supabase.from('versions').insert({
    transcript_id: transcriptId,
    user_id: user.id,
    transcript,
    saved_at: Date.now(),
    label: label ?? null,
  });
  if (error) throw error;
};

export const deleteVersion = async (
  _transcriptId: string,
  versionId: string,
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) return;
  const { error } = await supabase
    .from('versions')
    .delete()
    .eq('id', versionId)
    .eq('user_id', user.id);
  if (error) throw error;
};

// ── Comments ──────────────────────────────────────────────────────────────────

export const addComment = async (
  transcriptId: string,
  comment: Omit<TranscriptComment, 'id' | 'createdAt'>,
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) return;
  const { error } = await supabase.from('comments').insert({
    transcript_id: transcriptId,
    user_id: user.id,
    selected_text: comment.selectedText,
    start_offset: comment.startOffset,
    end_offset: comment.endOffset,
    comment: comment.comment,
    created_at: Date.now(),
  });
  if (error) throw error;
};

export const fetchComments = async (transcriptId: string): Promise<TranscriptComment[]> => {
  const user = auth.currentUser;
  if (!user) return [];
  const { data, error } = await supabase
    .from('comments')
    .select('*')
    .eq('transcript_id', transcriptId)
    .eq('user_id', user.id)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []).map(row => ({
    id: row.id as string,
    transcriptId: row.transcript_id as string,
    selectedText: row.selected_text as string,
    startOffset: row.start_offset as number,
    endOffset: row.end_offset as number,
    comment: row.comment as string,
    createdAt: row.created_at as number,
  }));
};

export const deleteComment = async (
  _transcriptId: string,
  commentId: string,
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) return;
  const { error } = await supabase
    .from('comments')
    .delete()
    .eq('id', commentId)
    .eq('user_id', user.id);
  if (error) throw error;
};
